import { useEffect, useMemo, useRef, useState } from 'react';

export type UseFakeProgressOptions = {
  /** Starting progress value (default: 25) */
  initialValue?: number;
  /** Tick interval in ms (default: 200) */
  intervalMs?: number;
  /** Progress ceiling before completion (default: 95) */
  maxValue?: number;
  /** Delay before hiding after completion in ms (default: 400) */
  completeDelayMs?: number;
  /** Fraction of remaining progress to increment per tick (default: 0.05) */
  incrementRate?: number;
  /** Minimum increment per tick (default: 0.5) */
  minIncrement?: number;
};

export type UseFakeProgressResponse = {
  progress: number;
  visible: boolean;
};

/**
 * NProgress-like fake progress hook for use with LinearProgress component in determinate mode.
 * Jumps to initialValue, slowly approaches maxValue, then completes to 100% when `active` becomes false.
 */
export const useFakeProgress = (
  active: boolean,
  {
    initialValue = 25,
    intervalMs = 200,
    maxValue = 95,
    completeDelayMs = 400,
    incrementRate = 0.05,
    minIncrement = 0.5,
  }: UseFakeProgressOptions = {},
): UseFakeProgressResponse => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const completeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (active) {
      // clear any pending completion when re-activating
      if (completeTimeout.current) {
        clearTimeout(completeTimeout.current);
        completeTimeout.current = null;
      }

      setVisible(true);
      setProgress(initialValue);

      const interval = setInterval(() => {
        setProgress(current => {
          if (current >= maxValue) {
            return current;
          }

          const increment = Math.max(
            minIncrement,
            (maxValue - current) * incrementRate,
          );

          return Math.min(maxValue, current + increment);
        });
      }, intervalMs);

      return () => clearInterval(interval);
    }

    // deactivating - jump to 100%, then hide after delay
    setProgress(100);
    completeTimeout.current = setTimeout(() => {
      setVisible(false);
      setProgress(0);
      completeTimeout.current = null;
    }, completeDelayMs);

    return () => {
      if (completeTimeout.current) {
        clearTimeout(completeTimeout.current);
        completeTimeout.current = null;
      }
    };
  }, [active]);

  return useMemo(() => ({ progress, visible }), [progress, visible]);
};
